import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BackButton } from '@components/ui/back-button/back-button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserTicket, UserTicketService, CreateMessageCommand, UserTicketMessageMessageGetQuery, UserTicketMessage, UserTicketBaseGetQuery } from '@6grain/masofa-monolith-api-client/common';
import { FileStorageItemService } from '@6grain/masofa-monolith-api-client/common/api/fileStorageItem.service';
import { of, firstValueFrom, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { Loading } from '@components/ui/loading/loading';
import { InputComponent } from '@components/input/input.component';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { ButtonComponent } from '@app/components/button/button.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { UserProfileService } from '@services/user-profile.service';
import { environment } from '@root/src/environments/environment';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';

interface ChatMessage {
  id: string;
  text: string;
  time: string;
  isOwn: boolean;
  isRead?: boolean;
  isSystem?: boolean;
  senderName?: string;
  attachments?: ChatAttachment[];
}

interface UploadAttachmentResponse {
  fileId?: string;
  FileId?: string;
  id?: string;
  Id?: string;
  fileName?: string;
  FileName?: string;
  fileSize?: number;
  FileSize?: number;
  contentType?: string;
  ContentType?: string;
}

interface ChatAttachment {
  id: string;
  url: string;
  displayName: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
}

interface AttachmentMetadata {
  id: string;
  fileName: string;
  contentType?: string;
  fileSize?: number;
}

@Component({
  selector: 'app-user-chat',
  templateUrl: './user-chat.component.html',
  imports: [
    FormsModule,
    CommonModule,
    BackButton,
    TranslatePipe,
    Loading,
    DialogModule,
    ButtonModule,
    TagModule,
    DateFormatLangPipe,
    ConfirmationModalComponent
  ],
  providers: [DatePipe],
  styleUrls: ['./user-chat.component.scss'],
  standalone: true,
})
export class UserChatComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  messageText = '';
  currentTicketId: string | null = null;
  currentUserTicket: UserTicket | null = null;
  loading = false;
  sendingMessage = false;

  messages: ChatMessage[] = [];
  loadingMessages = false;

  // File properties
  selectedFiles: File[] = [];
  uploadingFiles = false;
  showFileSendModal = false;
  currentUserName = '';
  currentAppealInfo: { moduleName?: string; exceptionType?: string; fullTicket?: UserTicket } = {};
  showAppealDetailsModal = false;
  showExceptionJson = false;
  showJsonDetailsModal = false;
  currentJsonData = '';

  // Confirmation modals
  showCloseTicketModal = false;
  showRejectTicketModal = false;
  pendingStatusAction: number | null = null;

  public translate: TranslateService;

  private attachmentMetadataCache = new Map<string, AttachmentMetadata>();
  private pendingAttachmentRequests = new Map<string, Promise<AttachmentMetadata | null>>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userTicketService: UserTicketService,
    private userProfileService: UserProfileService,
    private http: HttpClient,
    private fileStorageItemService: FileStorageItemService,
    translate: TranslateService
  ) {
    this.translate = translate;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const ticketId = params['id'];
      this.currentTicketId = ticketId || null;

      this.loadCurrentUser();
      this.loadUserTicket();
    });
  }

  private loadCurrentUser(): void {
    const userProfile = this.userProfileService.getProfile();
    this.currentUserName = userProfile?.userName || 'You';
  }

  private loadUserTicket(): void {
    if (!this.currentTicketId) return;

    this.loading = true;
    this.userTicketService.commonUserTicketGetByIdIdGet(this.currentTicketId)
      .pipe(
        catchError((error) => {
          this.loading = false;
          console.error('Error loading user ticket:', error);
          return of(null);
        }), takeUntil(this.destroy$)
      )
      .subscribe(userTicket => {
        this.loading = false;
        if (userTicket) {
          this.currentUserTicket = userTicket;
          this.loadAppealInfo();
          this.loadMessagesForChat();
        }
      });
  }

  private loadAppealInfo(): void {
    if (this.currentUserTicket) {
      this.currentAppealInfo = {
        moduleName: this.currentUserTicket.moduleName || 'Unknown Module',
        exceptionType: this.currentUserTicket.exceptionType || 'Unknown Type',
        fullTicket: this.currentUserTicket
      };
    }
  }

  showAppealDetails(): void {
    this.showAppealDetailsModal = true;
  }

  hideAppealDetails(): void {
    this.showAppealDetailsModal = false;
    this.showExceptionJson = false;
  }

  toggleExceptionJson(): void {
    this.showExceptionJson = !this.showExceptionJson;
  }

  showJsonModal(jsonData: string): void {
    this.currentJsonData = jsonData;
    this.showJsonDetailsModal = true;
  }

  hideJsonModal(): void {
    this.showJsonDetailsModal = false;
    this.currentJsonData = '';
  }

  isJsonLong(jsonData: string): boolean {
    return !!(jsonData && jsonData.length > 100);
  }

  getTruncatedJson(jsonData: string): string {
    if (!jsonData) return '';
    if (jsonData.length <= 100) return jsonData;
    return jsonData.substring(0, 100) + '...';
  }

  formatJson(jsonData: any): string {
    try {
      const parsed = JSON.parse(jsonData);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonData;
    }
  }

  private loadMessagesForChat(): void {
    if (!this.currentTicketId) return;

    this.loadingMessages = true;

    const messageQuery: UserTicketMessageMessageGetQuery = {
      take: 100,
      offset: 0,
      sortBy: "Created",
      sort: 1,
      filters: [
        {
          filterField: "UserTicketId",
          filterValue: this.currentTicketId,
          filterOperator: 0
        }
      ]
    };

    this.userTicketService.commonUserTicketGetMessageByQueryPost(messageQuery)
      .pipe(
        catchError(error => {
          this.loadingMessages = false;
          console.error('Error loading messages:', error);
          return of([]);
        })
      )
      .subscribe((apiMessages: UserTicketMessage[]) => {
        this.loadingMessages = false;

        this.messages = this.convertApiMessagesToChatMessages(apiMessages);
        setTimeout(() => this.scrollToBottom(), 100);
      });
  }

  private convertApiMessagesToChatMessages(apiMessages: UserTicketMessage[]): ChatMessage[] {
    if (!apiMessages || apiMessages.length === 0) {
      return [{
        id: 'system-1',
        text: this.translate.instant('chat.conversation_start') || 'Start of conversation',
        time: '',
        isOwn: false,
        isSystem: true
      }];
    }

    const sortedMessages = apiMessages.sort((a, b) => {
      const dateA = new Date(a.created || '').getTime();
      const dateB = new Date(b.created || '').getTime();
      return dateA - dateB;
    });

    const chatMessages: ChatMessage[] = [];

    chatMessages.push({
      id: 'system-start',
      text: this.formatDate(sortedMessages[0]?.created),
      time: '',
      isOwn: false,
      isSystem: true
    });

    sortedMessages.forEach(apiMessage => {
      const chatMessage: ChatMessage = {
        id: apiMessage.id || Date.now().toString(),
        text: apiMessage.message || '',
        time: this.formatTime(apiMessage.created),
        isOwn: this.isOwnMessage(apiMessage),
        isRead: true,
        senderName: this.isOwnMessage(apiMessage) ? this.currentUserName : 'Support Team'
      };

      const attachments = this.createAttachmentsFromIds(this.extractAttachmentIds(apiMessage));
      if (attachments.length) {
        chatMessage.attachments = attachments;
      }

      chatMessages.push(chatMessage);
    });

    return chatMessages;
  }

  private isOwnMessage(apiMessage: UserTicketMessage): boolean {
    const userProfile = this.userProfileService.getProfile();
    return apiMessage.emailId === userProfile?.id;
  }

  private extractAttachmentIds(apiMessage: UserTicketMessage): string[] {
    const directIds = (apiMessage as any)?.attachmentIds;
    if (Array.isArray(directIds) && directIds.length > 0) {
      return directIds
        .map(id => this.normalizeAttachmentId(id))
        .filter((id): id is string => !!id);
    }

    const attachmentIdsJson = (apiMessage as any)?.attachmentIdsJson;
    const parsed = this.parseAttachmentIdsJson(attachmentIdsJson);
    return parsed
      .map(id => this.normalizeAttachmentId(id))
      .filter((id): id is string => !!id);
  }

  private parseAttachmentIdsJson(jsonValue: any): any[] {
    if (!jsonValue) {
      return [];
    }

    try {
      const parsed = typeof jsonValue === 'string' ? JSON.parse(jsonValue) : jsonValue;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private normalizeAttachmentId(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object' && typeof value.toString === 'function') {
      const converted = value.toString();
      return converted && converted !== '[object Object]' ? converted : null;
    }

    return null;
  }

  private createAttachmentsFromIds(ids: string[], sourceFiles?: Array<{ name?: string }>): ChatAttachment[] {
    if (!ids || !ids.length) {
      return [];
    }

    const attachments = ids.map((id, index) => {
      const normalizedId = id.toString();
      return {
        id: normalizedId,
        url: this.buildAttachmentUrl(normalizedId),
        displayName: sourceFiles?.[index]?.name || `Attachment ${index + 1}`
      };
    });

    this.populateAttachmentMetadata(attachments);

    return attachments;
  }

  private buildAttachmentUrl(attachmentId: string): string {
    return `${environment.api}/common/FileStorageItem/GetFile/${attachmentId}`;
  }

  private populateAttachmentMetadata(attachments: ChatAttachment[]): void {
    attachments.forEach(attachment => {
      this.ensureAttachmentMetadata(attachment).catch(error => {
        console.error('Failed to populate attachment metadata', attachment.id, error);
      });
    });
  }

  private async ensureAttachmentMetadata(attachment: ChatAttachment): Promise<void> {
    if (!attachment || !attachment.id) {
      return;
    }

    if (this.attachmentMetadataCache.has(attachment.id)) {
      const metadata = this.attachmentMetadataCache.get(attachment.id);
      if (metadata) {
        this.applyAttachmentMetadata(attachment, metadata);
      }
      return;
    }

    if (!this.pendingAttachmentRequests.has(attachment.id)) {
      const request = firstValueFrom(this.fileStorageItemService.commonFileStorageItemGetByIdIdGet(attachment.id))
        .then(fileInfo => {
          if (!fileInfo) {
            return null;
          }

          const metadata = this.mapFileInfoToMetadata(attachment.id, fileInfo);
          this.attachmentMetadataCache.set(attachment.id, metadata);
          return metadata;
        })
        .catch(error => {
          console.error('Error fetching attachment metadata:', attachment.id, error);
          return null;
        })
        .finally(() => {
          this.pendingAttachmentRequests.delete(attachment.id);
        });

      this.pendingAttachmentRequests.set(attachment.id, request);
    }

    const metadata = await this.pendingAttachmentRequests.get(attachment.id);
    if (metadata) {
      this.applyAttachmentMetadata(attachment, metadata);
    }
  }

  private mapFileInfoToMetadata(id: string, fileInfo: any): AttachmentMetadata {
    const fileName =
      fileInfo?.fileName ||
      fileInfo?.FileName ||
      this.extractFileNameFromPath(fileInfo?.fileStoragePath || fileInfo?.FileStoragePath);

    const contentType = fileInfo?.fileContentTypeName || fileInfo?.FileContentTypeName || fileInfo?.fileContentType;
    const fileSize = fileInfo?.fileLength ?? fileInfo?.FileLength;

    return {
      id,
      fileName: fileName || `Attachment ${id}`,
      contentType: contentType || undefined,
      fileSize: typeof fileSize === 'number' ? fileSize : undefined
    };
  }

  private extractFileNameFromPath(path?: string): string {
    if (!path) {
      return '';
    }

    const segments = path.split(/[/\\]/).filter(Boolean);
    return segments.length ? segments[segments.length - 1] : path;
  }

  private applyAttachmentMetadata(attachment: ChatAttachment, metadata: AttachmentMetadata): void {
    if (!metadata) {
      return;
    }

    attachment.fileName = metadata.fileName || attachment.fileName;
    attachment.displayName = metadata.fileName || attachment.displayName;
    attachment.contentType = metadata.contentType || attachment.contentType;
    attachment.fileSize = metadata.fileSize ?? attachment.fileSize;
  }

  async downloadAttachment(attachment: ChatAttachment, event: Event): Promise<void> {
    event.preventDefault();

    if (!attachment?.id) {
      return;
    }

    await this.ensureAttachmentMetadata(attachment);

    const fileName = attachment.fileName || attachment.displayName || `attachment-${attachment.id}`;

    try {
      const blob = await firstValueFrom(
        this.http.get(attachment.url, {
          responseType: 'blob'
        })
      );

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      window.open(attachment.url, '_blank');
    }
  }
  private formatTime(dateString?: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();

      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (diffDays === 1) {
        return `Yesterday ${date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

    } catch {
      return '';
    }
  }

  private formatDate(dateString?: string): string {
    if (!dateString) return this.translate.instant('chat.today') || 'Today';

    try {
      const date = new Date(dateString);
      const today = new Date();

      if (date.toDateString() === today.toDateString()) {
        return this.translate.instant('chat.today') || 'Today';
      }

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === yesterday.toDateString()) {
        return this.translate.instant('chat.yesterday') || 'Yesterday';
      }

      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return this.translate.instant('chat.invalid_date') || 'Invalid date';
    }
  }

  async sendMessage(): Promise<void> {
    if ((!this.messageText.trim() && this.selectedFiles.length === 0) || !this.currentTicketId || !this.currentUserTicket || this.sendingMessage) return;

    this.sendingMessage = true;

    try {
      // Upload files if any
      let attachmentIds: string[] = [];
      if (this.selectedFiles.length > 0) {
        attachmentIds = await this.uploadFiles(this.selectedFiles);
      }

      const userProfile = this.userProfileService.getProfile();
      const createMessageCommand: CreateMessageCommand = {
        userTicketMessages: {
          userTicketId: this.currentTicketId,
          emailId: userProfile?.id || '',
          message: this.messageText.trim() || (this.selectedFiles.length > 0 ? 'File(s) attached' : ''),
          created: new Date().toISOString(),
          attachmentIds: attachmentIds,
          attachmentIdsJson: JSON.stringify(attachmentIds)
        },
        sendEmail: true,
        toEmail: this.currentUserTicket.createUserEmail || ''
      };

      this.userTicketService.commonUserTicketCreateMessagePost(createMessageCommand)
        .pipe(
          catchError(error => {
            this.sendingMessage = false;
            console.error('Error sending message:', error);
            return of(null);
          })
        )
        .subscribe(response => {
          this.sendingMessage = false;

          if (response) {
            const chatMessage: ChatMessage = {
              id: Date.now().toString(),
              text: this.messageText.trim() || (this.selectedFiles.length > 0 ? `${this.selectedFiles.length} file(s) sent` : ''),
              time: new Date().toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
              }),
              isOwn: true,
              isRead: false,
              senderName: this.currentUserName
            };

            const attachments = this.createAttachmentsFromIds(attachmentIds, this.selectedFiles);
            if (attachments.length) {
              chatMessage.attachments = attachments;
            }

            this.messages.push(chatMessage);
            this.messageText = '';
            this.selectedFiles = [];

            setTimeout(() => {
              this.scrollToBottom();
            }, 100);
          }
        });
    } catch (error) {
      this.sendingMessage = false;
      console.error('Error in sendMessage:', error);
    }
  }

  private buildAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  scrollToBottom(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement as HTMLElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  onAttachmentClick(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
      this.showFileSendModal = true;
    }
  }

  onFileSend(filesWithCaptions: Array<{ file: File; caption: string }>): void {
    this.sendFilesWithCaptions(filesWithCaptions);
  }

  onFileSendModalClose(): void {
    this.showFileSendModal = false;
    this.selectedFiles = [];
  }

  sendFiles(): void {
    if (this.selectedFiles.length > 0) {
      const filesWithCaptions = this.selectedFiles.map(file => ({ file, caption: '' }));
      this.sendFilesWithCaptions(filesWithCaptions);
    }
  }

  private async sendFilesWithCaptions(filesWithCaptions: Array<{ file: File; caption: string }>): Promise<void> {
    if (!this.currentTicketId || !this.currentUserTicket || this.sendingMessage) return;

    this.sendingMessage = true;

    try {
      // Upload files
      const uploadedFileIds = await this.uploadFiles(filesWithCaptions.map(fileItem => fileItem.file));

      // Send message with files and captions
      const messageText = filesWithCaptions[0]?.caption ||
                         (filesWithCaptions.length > 1 ?
                          `${filesWithCaptions.length} files sent` :
                          'File sent');

      const userProfile = this.userProfileService.getProfile();
      const createMessageCommand: CreateMessageCommand = {
        userTicketMessages: {
          userTicketId: this.currentTicketId,
          emailId: userProfile?.id || '',
          message: messageText,
          created: new Date().toISOString(),
          attachmentIds: uploadedFileIds,
          attachmentIdsJson: JSON.stringify(uploadedFileIds)
        },
        sendEmail: true,
        toEmail: this.currentUserTicket.createUserEmail || ''
      };


      this.userTicketService.commonUserTicketCreateMessagePost(createMessageCommand)
        .pipe(
          catchError(error => {
            this.sendingMessage = false;
            console.error('Error sending message with files:', error);
            return of(null);
          })
        )
        .subscribe(response => {
          this.sendingMessage = false;
          console.log('Message with files sent successfully:', response);

          if (response) {
            const chatMessage: ChatMessage = {
              id: Date.now().toString(),
              text: messageText,
              time: new Date().toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
              }),
              isOwn: true,
              isRead: false,
              senderName: this.currentUserName
            };

            const attachments = this.createAttachmentsFromIds(uploadedFileIds, filesWithCaptions.map(item => item.file));
            if (attachments.length) {
              chatMessage.attachments = attachments;
            }

            this.messages.push(chatMessage);
            this.selectedFiles = [];
            this.showFileSendModal = false;

            setTimeout(() => {
              this.scrollToBottom();
            }, 0);
          }
        });
    } catch (error) {
      this.sendingMessage = false;
      console.error('Error in sendFilesWithCaptions:', error);
    }
  }

  private async uploadFiles(files: File[]): Promise<string[]> {
    if (!this.currentTicketId || files.length === 0) {
      return [];
    }

    this.uploadingFiles = true;
    const uploadedFileIds: string[] = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('UserTicketId', this.currentTicketId);
        formData.append('File', file, file.name);

        const response = await firstValueFrom(
          this.http.post<UploadAttachmentResponse>(
            `${environment.api}/common/UserTicket/UploadAttachment`,
            formData,
            { headers: this.buildAuthHeaders() }
          )
        );

        const fileId = response?.fileId ?? response?.FileId ?? response?.id ?? response?.Id;
        if (fileId) {
          uploadedFileIds.push(fileId);
        } else {
          console.warn('Upload response missing file identifier', response);
        }
      }

      return uploadedFileIds;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      this.uploadingFiles = false;
    }
  }

  removeSelectedFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  getFileSize(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
    return Math.round(size / (1024 * 1024)) + ' MB';
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'pi-file-pdf';
      case 'doc':
      case 'docx': return 'pi-file-word';
      case 'xls':
      case 'xlsx': return 'pi-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'pi-image';
      case 'zip':
      case 'rar': return 'pi-file-zip';
      default: return 'pi-file';
    }
  }

  goBack(): void {
    this.router.navigate(['/help/support-tickets']);
  }

  getStatusLabel(status: number): string {
    const statusLabels: Record<number, string> = {
      0: this.translate.instant('New'),
      1: this.translate.instant('In Progress'),
      2: this.translate.instant('Rejected'),
      3: this.translate.instant('Complete')
    };
    return statusLabels[status] || 'Unknown';
  }

  getStatusSeverity(status: number): string {
    const severityMap: Record<number, string> = {
      0: 'success',
      1: 'info',
      2: 'danger',
      3: 'success'
    };
    return severityMap[status] || 'secondary';
  }

  // Check if status change buttons should be shown
  // On user page, always show buttons when status is InProgress (1) - user is always the creator
  showStatusButtons(): boolean {
    if (!this.currentUserTicket) return false;
    // Show buttons only when status is InProgress (1)
    return this.currentUserTicket.ticketStatus === 1;
  }

  // Update ticket status
  updateTicketStatus(newStatus: number): void {
    if (!this.currentUserTicket || !this.currentTicketId) {
      return;
    }

    this.loading = true;
    const updatedTicket: UserTicket = {
      ...this.currentUserTicket,
      ticketStatus: newStatus as any
    };

    this.userTicketService.commonUserTicketUpdatePut(updatedTicket)
      .pipe(
        catchError((error) => {
          this.loading = false;
          console.error('Error updating ticket status:', error);
          alert(this.translate.instant('Failed to update ticket status'));
          return of(null);
        })
      )
      .subscribe((updated) => {
        this.loading = false;
        if (updated) {
          this.currentUserTicket = updated;
          this.loadAppealInfo();
          // Reload messages to refresh UI
          this.loadMessagesForChat();
        }
      });
  }

  // Close ticket (status 3 - Complete)
  closeTicket(): void {
    this.pendingStatusAction = 3;
    this.showCloseTicketModal = true;
  }

  // Reject ticket (status 2 - Rejected)
  rejectTicket(): void {
    this.pendingStatusAction = 2;
    this.showRejectTicketModal = true;
  }

  // Handle confirmation for status change
  onConfirmStatusChange(): void {
    if (this.pendingStatusAction !== null) {
      this.updateTicketStatus(this.pendingStatusAction);
      this.pendingStatusAction = null;
    }
  }

  // Handle cancel for status change
  onCancelStatusChange(): void {
    this.pendingStatusAction = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
